import _ from 'lodash';
import { v4 as UUID } from 'uuid';
import EventBus from '@/app/eventBus';
import { ItemStatus } from '@/app/utils/constants/ItemStatus';
import { ToolkitApi } from '@/app/services/api/toolkit';
import { types } from '@/app/filters/filters';
import { getLocalType } from '@/app/utils/toolkit/defaultItems';

const fund = {
  uuid: undefined,
  status: ItemStatus.STARTED,
  permissions: {
    cognitoID: undefined,
    accountID: undefined,
  },
  details: {
    address: undefined,
    contactNumber: undefined,
    name: '',
  },
  members: [],
  trustee: {
    type: '',
    trustees: [],
    company: {
      name: '',
    },
  },
};

const mutations = {
  CLEAR_DIRTY(state) {
    state.dirty = false;
  },
  SET_FUND_UUID(state, uuid) {
    state.dirty = true;
    state.fund.uuid = uuid;
  },
  SET_FUND_OWNER(state, ownerUUID) {
    state.dirty = true;
    state.fund.permissions.cognitoID = ownerUUID;
  },
  SET_FUND_ACCOUNT(state, accountUUID) {
    state.dirty = true;
    state.fund.permissions.accountID = accountUUID;
  },
  SET_FUND_DETAILS(state, details) {
    state.dirty = true;
    state.fund.details = details;
  },
  SET_FUND_MEMBERS(state, members) {
    state.dirty = true;
    state.fund.members = members;
  },
  SET_FUND_TRUSTEE(state, trustee) {
    state.dirty = true;
    state.fund.trustee = trustee;
  },
  SET_FUND_STATUS(state, status) {
    state.dirty = true;
    state.fund.status = status;
  },
  RESET_FUND(state) {
    state.fund = _.cloneDeep(fund);
  },
};

const actions = {
  saveFund({ commit, state, rootGetters }) {
    if (state.dirty) {
      return ToolkitApi.saveItem({
        uuid: state.fund.uuid || undefined,
        type: rootGetters['toolkit/getItemType'],
        accountID: rootGetters['account/getActiveAccount'].uuid,
        relation: rootGetters['account/getActiveAccount'].uuid,
        cognitoID: state.fund.permissions.cognitoID,
        status: state.fund.status,
        fundName: state.fund.details.name,
        fund: {
          details: state.fund.details,
          members: state.fund.members,
          trustee: state.fund.trustee,
        },
        data: rootGetters['toolkit/getItemData'],
      })
        .then((res) => {
          commit('SET_FUND_UUID', res.data.data.uuid);
          commit('CLEAR_DIRTY');
          // TODO: Refactor EventBus to a notification store module - Remove EventBus
          EventBus.$emit('addNotification', {
            title: `${types(getLocalType(rootGetters['toolkit/getItemType']))} Saved!`,
            icon: 'tick',
            message: `${types(getLocalType(rootGetters['toolkit/getItemType']))} for ${
              state.fund.details.name
            }`,
          });
          return res;
        })
        .catch((err) => {
          // TODO: Refactor EventBus to a notification store module - Remove EventBus
          EventBus.$emit('addNotification', {
            title: `${types(getLocalType(rootGetters['toolkit/getItemType']))} Save Failed!`,
            icon: 'cross',
            message: `${types(getLocalType(rootGetters['toolkit/getItemType']))} for ${
              state.fund.details.name
            } failed to save: ${err}`,
          });
          // TODO: Create Global error component for error handling
          throw new Error(err);
        });
    }
    return false;
  },
  updateOwner({ commit, rootGetters }) {
    commit('SET_FUND_OWNER', rootGetters['auth/getCognitoID']);
  },
  updateFundDetails({ commit, state }, details) {
    const newDetails = {
      ...state.fund.details,
      ...details,
    };
    if (!_.isEqual(newDetails, state.fund.details)) {
      commit('SET_FUND_DETAILS', newDetails);
    }
  },
  updateFundMembers({ commit }, members) {
    members.forEach((member) => {
      if (!member.uuid) {
        member.uuid = UUID();
      }
    });
    commit('SET_FUND_MEMBERS', members);
  },
  updateFundTrustee({ commit, state }, trustee) {
    const newTrustee = {
      ...state.fund.trustee,
      ...trustee,
    };
    commit('SET_FUND_TRUSTEE', newTrustee);
  },
  updateFundStatus({ commit }, status) {
    commit('SET_FUND_STATUS', status);
  },
  async loadFund({ commit, dispatch }, fundPayload) {
    await dispatch('resetFund', null, { root: true });
    fundPayload.members.forEach((member) => {
      if (!member.uuid) {
        member.uuid = UUID();
      }
      member.dateOfBirth = new Date(member.dateOfBirth);
    });

    fundPayload.trustee.trustees.forEach((trustee) => {
      trustee.dateOfBirth = new Date(trustee.dateOfBirth);
      if (!trustee.uuid) {
        const matchedMember = fundPayload.members.find(member => trustee.isMember
              && trustee.firstName === member.firstName
              && trustee.lastName === member.lastName
              && trustee.dateOfBirth.toString() === member.dateOfBirth.toString());
        if (matchedMember) {
          trustee.uuid = matchedMember.uuid;
        } else {
          trustee.uuid = UUID();
        }
      }
    });

    const newFund = _.cloneDeep(fund);
    const item = _.merge(newFund, fundPayload);

    commit('SET_FUND_UUID', item.uuid);
    commit('SET_FUND_STATUS', item.status);
    commit('SET_FUND_OWNER', item.cognitoID);
    commit('SET_FUND_ACCOUNT', fundPayload.relation);
    commit('SET_FUND_DETAILS', item.details);
    commit('SET_FUND_MEMBERS', item.members);
    commit('SET_FUND_TRUSTEE', item.trustee);
    commit('CLEAR_DIRTY');
  },
  resetFund: {
    root: true,
    handler: function resetFund({ commit }) {
      commit('RESET_FUND');
    },
  },
};

const getters = {
  isDirty(state) {
    return state.dirty;
  },
  getFundOwner(state) {
    return state.fund.permissions.cognitoID || undefined;
  },
  getFundAccount(state) {
    return state.fund.permissions.accountID || undefined;
  },
  hasFund(state) {
    return !!state.fund.permissions.cognitoID;
  },
  getFund(state) {
    return state.fund;
  },
  getFundMembers(state) {
    return state.fund.members.map(member => ({
      ...member,
      displayName: `${member.title} ${member.firstName} ${member.lastName}`,
    }));
  },
  canEditActiveFund(state, localGetters, rootState, rootGetters) {
    const loggedInUser = rootGetters['auth/getCognitoID'];
    return state.fund
      ? state.fund.permissions.cognitoID === loggedInUser
      : false;
  },

};

const state = {
  dirty: false,
  fund: _.cloneDeep(fund),
};

const store = {
  namespaced: true,
  mutations,
  actions,
  getters,
  state,
};

export default store;
